import {push} from 'connected-react-router';

import {
  INITIALISE,
  SET_PAGE,
  SET_CATEGORY,
  SET_CATEGORIES,
  SET_AUTHOR,
  SET_AUTHORS,
  SET_SEARCH,
  SET_ARTICLES,
  SET_LOADING,
  SET_PRIORITY_MODULES,
  HomeActionTypes} from './Types';
import {IHttpResponse} from '../../Services/Service';
import {Home} from '../../Services/HomeService';
import {Article} from '../../Services/ArticleService';
import {Page} from '../../Models';
import {ThunkResult} from '../index';
import {clearArticle} from '../Articles/Actions';

export const initialiseHome = (home: Home): HomeActionTypes => {
  return {
    type: INITIALISE,
    payload: home,
  };
};

export const setPage = (page: number): HomeActionTypes => {
  return {
    type: SET_PAGE,
    payload: page,
  };
};

export const setCategories = (categories: string[]) => {
  return {
    type: SET_CATEGORIES,
    payload: categories,
  };
};

export const setCategory = (category: string) => {
  return {
    type: SET_CATEGORY,
    payload: category,
  };
};

export const setAuthor = (id: number, name: string) => {
  return {
    type: SET_AUTHOR,
    payload: {
      id, name,
    },
  };
};

export const setAuthors = (authors: Record<number, string>) => {
  return {
    type: SET_AUTHORS,
    payload: authors,
  };
};

export const setSearch = (search: string) => {
  return {
    type: SET_SEARCH,
    payload: search,
  };
};

export const setArticles = (articles: Page<Article>) => {
  return {
    type: SET_ARTICLES,
    payload: articles,
  };
};

export const setLoading = (loading: boolean) => {
  return {
    type: SET_LOADING,
    payload: loading,
  };
};

export const setPriorityModules = (modules: string[]) => {
  return {
    type: SET_PRIORITY_MODULES,
    payload: modules,
  };
};

const filterArticles = (): ThunkResult<Promise<void>> => {
  return async (dispatch, getState) => {
    const {
      auth: {authorised, articleService},
      home: {
        currentCategory,
        currentAuthor,
        currentSearch,
        currentPage,
        loading,
        priorityModules,
      },
      router: {location: {pathname}},
    } = getState();

    if (!authorised || loading) {
      return;
    }

    dispatch(setLoading(true));

    let articles: Promise<IHttpResponse<Page<Article>>>;
    let newPath : string;
    if (currentCategory !== 'All') {
      articles = articleService.listByCategory(currentCategory, currentPage);
      newPath = `/public/category/${currentCategory}`;
    } else if (currentAuthor !== -1) {
      articles = articleService.listByAuthor(currentAuthor, currentPage);
      newPath = `/public/author/${currentAuthor}`;
    } else if (currentSearch !== '') {
      articles = articleService.search(currentSearch, currentPage);
      newPath = `/public/search/${currentSearch}`;
    } else {
      articles = articleService.list(currentPage, priorityModules);
      newPath = '/public';
    }

    try {
      const res = await articles;
      if (res.parsedBody !== undefined) {
        dispatch(setArticles(res.parsedBody));

        if (pathname !== newPath) {
          dispatch(push(newPath));
        }
      }
    } catch (e) {
      console.log(e);
    }

    dispatch(setLoading(false));

    dispatch(clearArticle());
  };
};

export const setPageCreator = (page: number): ThunkResult<Promise<void>> => {
  return async (dispatch) => {
    dispatch(setPage(page));
    dispatch(filterArticles());
  };
};

export const setCategoryCreator = (category: string): ThunkResult<Promise<void>> => {
  return async (dispatch) => {
    dispatch(setAuthor(-1, ''));
    dispatch(setCategory(category));
    dispatch(setSearch(''));
    dispatch(setPage(1));
    dispatch(filterArticles());
  };
};

export const setAuthorCreator = (id: number): ThunkResult<Promise<void>> => {
  return async (dispatch, getState) => {
    const {
      home: {
        authors,
      },
      auth: {
        authorService,
      },
    } = getState();

    let authorName = authors[id];
    if (authorName === undefined) {
      try {
        const res = await authorService.byId(id);
        if (res.parsedBody === undefined) {
          authorName = '';
        } else {
          authorName = res.parsedBody.name;
        }
      } catch (e) {
        console.log(e);
      }

      authors[id] = authorName;
      dispatch(setAuthors(authors));
    }

    dispatch(setCategory('All'));
    dispatch(setAuthor(id, authorName));
    dispatch(setSearch(''));
    dispatch(setPage(1));
    dispatch(filterArticles());
  };
};

export const setSearchCreator = (search: string): ThunkResult<Promise<void>> => {
  return async (dispatch) => {
    dispatch(setAuthor(-1, ''));
    dispatch(setCategory('All'));
    dispatch(setSearch(search));
    dispatch(setPage(1));
    dispatch(filterArticles());
  };
};

export const pushHomeCreator = (): ThunkResult<Promise<void>> => {
  return async (dispatch) => {
    dispatch(push('/public'));
    dispatch(setAuthor(-1, ''));
    dispatch(setCategory('All'));
    dispatch(setSearch(''));
    dispatch(setPage(1));
    dispatch(filterArticles());
  };
};

export const loadPriorityModulesCreator = (): ThunkResult<Promise<string[]>> => {
  return async (dispatch, getState) => {
    const {
      auth: {officeCompanionService},
    } = getState();

    const modules = await officeCompanionService.getModules();
    dispatch(setPriorityModules(modules));
    return modules;
  };
};
